import { Plus, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebouncedSearch } from '@/lib/hooks/use-debounced-search';

import { PROTOTYPE_SORT_OPTIONS, type PrototypeSort } from './prototypes-search';

export interface PrototypesToolbarProps {
  /** The committed search term, straight from the URL. */
  search: string;
  sort: PrototypeSort;
  onSearchChange: (search: string) => void;
  onSortChange: (sort: PrototypeSort) => void;
  onCreate: () => void;
  /** Whether the current role may create a prototype (spec matrix). */
  canCreate: boolean;
  /** True while a request is in flight, for the input's busy state. */
  isFetching: boolean;
}

/**
 * Search, sort and create for the prototype list (spec R6.6).
 *
 * The same shape as the project toolbar, and deliberately so: the two lists sit
 * one click apart, and a control that moved or renamed itself between them would
 * be a difference with nothing behind it. The input debounces against the URL,
 * so a filtered project page is a link that can be sent to someone.
 */
export const PrototypesToolbar = ({
  search,
  sort,
  onSearchChange,
  onSortChange,
  onCreate,
  canCreate,
  isFetching,
}: PrototypesToolbarProps) => {
  const [draft, setDraft, clearDraft] = useDebouncedSearch(search, onSearchChange);

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative w-full sm:max-w-xs">
        <Search
          aria-hidden="true"
          strokeWidth={1.75}
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          placeholder="Search prototypes"
          aria-label="Search prototypes by name or description"
          aria-busy={isFetching}
          className="pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
        />
        {draft === '' ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={clearDraft}
            className="absolute right-1 top-1/2 size-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X aria-hidden="true" />
            <span className="sr-only">Clear search</span>
          </Button>
        )}
      </div>

      <Select
        value={sort}
        onValueChange={(value) => {
          onSortChange(value as PrototypeSort);
        }}
      >
        <SelectTrigger className="w-full sm:w-48" aria-label="Sort prototypes">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PROTOTYPE_SORT_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {canCreate ? (
        <Button type="button" onClick={onCreate} className="sm:ml-auto">
          <Plus aria-hidden="true" />
          New prototype
        </Button>
      ) : null}
    </div>
  );
};
